import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["Caret", "children", "actions", "ContentWrapper", "disabled", "draggable", "DragHandle", "Header", "hovered", "indentation", "onOpenChange", "open", "Opener", "titleText", "Title", "use"],
  _excluded2 = ["hovered"];
import { Children } from 'react';
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import { Clickable } from '../Clickable/Clickable';
import { ExpandableContents } from '../ExpandableContents/ExpandableContents';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import styled from 'foundations-theming-specialty/utils';
import { Text } from '../../../components/Text/Text';
import { HoverProvider } from '../../utils/useHovered';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { DragHandle } from '../DragHandle';
import { Controllable } from '../Controllable';
import { AccordionTransition } from '../AccordionTransition/AccordionTransition';
import { MICRO_TRANSITION_TIMING } from '../../../constants/times';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ExpandableWrapper = styled.div.withConfig({
  displayName: "ExpandableSection__ExpandableWrapper"
})(({
  $cssStyles
}) => Object.assign({}, $cssStyles));
const StyledCaretWrapper = styled.div.withConfig({
  displayName: "ExpandableSection__StyledCaretWrapper"
})(({
  $open,
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: `all ${MICRO_TRANSITION_TIMING} ease-in-out`,
  transform: $open ? 'rotate(90deg)' : undefined,
  marginInlineEnd: theme.space['300']
}));
const ExpandableCaret = ({
  open
}) => {
  return /*#__PURE__*/_jsx(StyledCaretWrapper, {
    role: "img",
    $open: open,
    children: /*#__PURE__*/_jsx(RightIcon, {
      color: "icon-interactive-default"
    })
  });
};
const ExpandableContentWrapper = ({
  children,
  open,
  indentation,
  use
}) => {
  return /*#__PURE__*/_jsx(ExpandableContents, {
    "aria-hidden": open ? 'false' : 'true',
    indentation: indentation,
    use: use,
    children: children
  });
};
const ExpandableHeader = styled.div.withConfig({
  displayName: "ExpandableSection__ExpandableHeader"
})(({
  use,
  theme
}) => {
  return Object.assign({
    position: 'relative',
    minHeight: '55px',
    display: 'flex',
    justifyContent: 'space-between',
    paddingInlineStart: theme.space['400'],
    paddingInlineEnd: theme.space['500'],
    paddingBlock: theme.space['250'],
    borderBlockStart: `${theme.borderWidth['100']} solid ${theme.color['border-transitional-core-subtle']}`
  }, use === 'enclosed' && {
    backgroundColor: theme.color['fill-field-default'],
    borderBlockEnd: `${theme.borderWidth['100']} solid ${theme.color['border-transitional-core-subtle']}`,
    marginBlockStart: '-1px'
  });
});
const ExpandableButton = styled(Clickable)(({
  disabled,
  theme
}) => {
  return {
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 auto',
    userSelect: 'none',
    '> h2': Object.assign({
      lineHeight: 'normal'
    }, disabled ? {
      color: theme.color['text-core-disabled']
    } : {
      '&:hover': {
        color: theme.color['specialty-text-interactive-alt-hover']
      }
    })
  };
});
const ExpandableTitle = ({
  titleText
}) => {
  return /*#__PURE__*/_jsx(Text, {
    as: "h2",
    typography: "heading-50",
    role: "presentation",
    children: titleText
  });
};
const ExpandableActions = styled.div.withConfig({
  displayName: "ExpandableSection__ExpandableActions"
})(({
  theme,
  $singleAction
}) => {
  return Object.assign({
    display: 'flex',
    alignItems: 'center',
    marginInlineStart: theme.space['200']
  }, $singleAction ? {
    // apply margin between children of the child (non-react fragment) that wraps the actual actions
    '& > *:first-child > * + *': {
      marginLeft: theme.space['300']
    }
  } : {
    // applies margin between each action (actions are wrapped in react fragment)
    '& > * + *': {
      marginLeft: theme.space['300']
    }
  });
});
const ExpandableDragHandleHolder = styled.div.withConfig({
  displayName: "ExpandableSection__ExpandableDragHandleHolder"
})(({
  hovered
}) => {
  return {
    position: 'absolute',
    left: '8px',
    display: 'flex',
    alignItems: 'center',
    opacity: hovered ? '1' : '0',
    top: '50%',
    transform: 'translateY(-50%)',
    transition: `opacity ${MICRO_TRANSITION_TIMING} ease-out`
  };
});
const ExpandableSectionComponent = props => {
  const {
      Caret = ExpandableCaret,
      children,
      actions,
      ContentWrapper = ExpandableContentWrapper,
      disabled = false,
      draggable = false,
      DragHandle: ExpandableDragHandle = DragHandle,
      Header = ExpandableHeader,
      indentation = 'default',
      onOpenChange,
      open = false,
      Opener = ExpandableButton,
      titleText,
      Title = ExpandableTitle,
      use = 'default'
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, props, {
    children: hoverProviderProps => {
      const {
          hovered
        } = hoverProviderProps,
        hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded2);
      return /*#__PURE__*/_jsxs(ExpandableWrapper, Object.assign({}, domProps, hoverProviderRestProps, {
        $cssStyles: cssStyles,
        [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: 'ExpandableSection',
        children: [/*#__PURE__*/_jsxs(Header, {
          use: use,
          children: [draggable && /*#__PURE__*/_jsx(ExpandableDragHandleHolder, {
            hovered: hovered,
            children: /*#__PURE__*/_jsx(ExpandableDragHandle, {})
          }), titleText && /*#__PURE__*/_jsxs(Opener, {
            "aria-expanded": open ? 'true' : 'false',
            disabled: disabled,
            onClick: onOpenChange && (() => {
              onOpenChange(syntheticEvent(!open));
            }),
            children: [/*#__PURE__*/_jsx(Caret, {
              open: open
            }), /*#__PURE__*/_jsx(Title, {
              titleText: titleText
            })]
          }), actions && /*#__PURE__*/_jsx(ExpandableActions, {
            $singleAction: Children.toArray(actions).length === 1,
            children: actions
          })]
        }), /*#__PURE__*/_jsx(AccordionTransition, {
          open: open,
          children: /*#__PURE__*/_jsx(ContentWrapper, {
            open: open,
            indentation: indentation,
            use: use,
            children: children
          })
        })]
      }));
    }
  }));
};
const defaultProps = {
  open: false
};
export const ExpandableSection = Controllable(ExpandableSectionComponent, ['open'], defaultProps);
ExpandableSection.displayName = 'ExpandableSection';